import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  User,
  Users,
  Euro,
  TrendingUp,
  Scissors,
  Calendar,
  ChevronRight,
  Award,
  Target,
  BarChart3,
  Clock,
  Phone,
  CreditCard,
  ArrowUpCircle
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export default function ReportOperatori({ variant = "full" }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("mese");
  const [report, setReport] = useState(null);
  const [selectedOperatore, setSelectedOperatore] = useState(null);
  const [dettaglioOpen, setDettaglioOpen] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [loadingDettaglio, setLoadingDettaglio] = useState(false);

  // Opzioni periodo estese
  const periodi = variant === "compact" 
    ? [
        { value: "oggi", label: "Oggi" },
        { value: "settimana", label: "Settimana" },
        { value: "mese", label: "Mese" },
      ]
    : [
        { value: "oggi", label: "Oggi" },
        { value: "ieri", label: "Ieri" },
        { value: "settimana", label: "Settimana" },
        { value: "2settimane", label: "2 Settimane" },
        { value: "mese", label: "Mese" },
        { value: "3mesi", label: "3 Mesi" },
        { value: "anno", label: "Anno" },
      ];

  useEffect(() => {
    fetchReport();
  }, [periodo]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/report-operatori?periodo=${periodo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  };

  const openDettaglio = async (operatore) => {
    setSelectedOperatore(operatore);
    setDettaglioOpen(true);
    setLoadingDettaglio(true);
    
    try {
      const response = await axios.get(
        `${API}/analytics/report-operatore/${operatore.operatore_id}?periodo=${periodo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDettaglio(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dettaglio");
    } finally {
      setLoadingDettaglio(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!report) return null;

  // Variante compatta per Cassa
  if (variant === "compact") {
    return (
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Report Operatori
            </CardTitle>
            <Select value={periodo} onValueChange={setPeriodo}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periodi.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {report.operatori.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">Nessun dato disponibile</p>
          ) : (
            report.operatori.slice(0, 5).map((op, idx) => (
              <div 
                key={op.operatore_id}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
                onClick={() => openDettaglio(op)}
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: op.colore }}
                >
                  {idx === 0 ? <Award className="w-5 h-5" /> : op.nome.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{op.nome}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>{op.num_clienti} clienti</span>
                    <span>•</span>
                    <span>{op.num_transazioni} op.</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-800">€{op.incasso_totale}</p>
                  <p className="text-xs text-emerald-600 font-medium">{op.pct_economia}%</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            ))
          )}
        </CardContent>
        
        {/* Dialog Dettaglio */}
        <DettaglioOperatoreDialog 
          open={dettaglioOpen}
          onOpenChange={setDettaglioOpen}
          operatore={selectedOperatore}
          dettaglio={dettaglio}
          loading={loadingDettaglio}
        />
      </Card>
    );
  }

  // Variante completa per Analisi
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Report per Operatore
          </CardTitle>
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periodi.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Periodo */}
        <div className="text-sm text-slate-500 bg-slate-50 rounded-lg px-4 py-2 inline-block">
          Periodo: <span className="font-medium text-slate-700">
            {report.periodo?.inizio} - {report.periodo?.fine}
          </span>
        </div>

        {/* Totali Salone */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
          <div className="text-center p-3 sm:p-4 bg-blue-50 rounded-xl min-w-0">
            <Euro className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-blue-600 mb-1 sm:mb-2" />
            <p className="text-lg sm:text-2xl font-bold text-blue-700 truncate" title={`€${report.totale_salone.incasso}`}>€{report.totale_salone.incasso}</p>
            <p className="text-[10px] sm:text-xs text-slate-500">Incasso Totale</p>
          </div>
          <div className="text-center p-3 sm:p-4 bg-emerald-50 rounded-xl min-w-0">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-emerald-600 mb-1 sm:mb-2" />
            <p className="text-lg sm:text-2xl font-bold text-emerald-700">{report.totale_salone.appuntamenti}</p>
            <p className="text-[10px] sm:text-xs text-slate-500">Appuntamenti</p>
          </div>
          <div className="text-center p-3 sm:p-4 bg-purple-50 rounded-xl min-w-0">
            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-purple-600 mb-1 sm:mb-2" />
            <p className="text-lg sm:text-2xl font-bold text-purple-700">{report.totale_salone.transazioni}</p>
            <p className="text-[10px] sm:text-xs text-slate-500">Transazioni</p>
          </div>
          <div className="text-center p-3 sm:p-4 bg-pink-50 rounded-xl min-w-0" data-testid="kpi-card-vendute">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-pink-600 mb-1 sm:mb-2" />
            <p className="text-lg sm:text-2xl font-bold text-pink-700">
              {report.totale_salone.card_vendute_count || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-slate-500">
              Card Vendute <span className="font-semibold">€{(report.totale_salone.card_vendute_importo || 0).toFixed(0)}</span>
            </p>
          </div>
          <div className="text-center p-3 sm:p-4 bg-amber-50 rounded-xl min-w-0" data-testid="kpi-card-ricariche">
            <ArrowUpCircle className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-amber-600 mb-1 sm:mb-2" />
            <p className="text-lg sm:text-2xl font-bold text-amber-700">
              {report.totale_salone.card_ricariche_count || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-slate-500">
              Ricariche <span className="font-semibold">€{(report.totale_salone.card_ricariche_importo || 0).toFixed(0)}</span>
            </p>
          </div>
        </div>

        {/* Lista Operatori */}
        {report.operatori.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessun dato disponibile per questo periodo</p>
          </div>
        ) : (
          <div className="space-y-4">
            {report.operatori.map((op, idx) => (
              <div 
                key={op.operatore_id}
                className="p-4 bg-white border border-slate-200 rounded-2xl hover:shadow-md transition-all cursor-pointer"
                onClick={() => openDettaglio(op)}
              >
                <div className="flex items-start gap-4">
                  {/* Avatar e Posizione */}
                  <div className="relative">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl"
                      style={{ backgroundColor: op.colore }}
                    >
                      {op.nome.charAt(0)}
                    </div>
                    {idx < 3 && (
                      <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-slate-300 text-slate-700' :
                        'bg-amber-600 text-amber-100'
                      }`}>
                        {idx + 1}°
                      </div>
                    )}
                  </div>

                  {/* Info Principale */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <h3 className="font-bold text-base sm:text-lg text-slate-800 break-words flex-1 min-w-0" title={op.nome}>{op.nome}</h3>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xl sm:text-2xl font-black text-slate-900">€{op.incasso_totale}</span>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {op.num_clienti} clienti
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {op.num_transazioni} operazioni
                      </span>
                      {op.card_vendute_count > 0 && (
                        <span className="flex items-center gap-1 text-pink-600 font-medium" title="Card vendute">
                          <CreditCard className="w-4 h-4" />
                          {op.card_vendute_count} card €{op.card_vendute_importo}
                        </span>
                      )}
                      {op.card_ricariche_count > 0 && (
                        <span className="flex items-center gap-1 text-amber-600 font-medium" title="Ricariche card">
                          <ArrowUpCircle className="w-4 h-4" />
                          {op.card_ricariche_count} ricariche €{op.card_ricariche_importo}
                        </span>
                      )}
                    </div>

                    {/* Progress Bar Economia */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">% Economia Salone</span>
                        <span className="font-bold text-emerald-600">{op.pct_economia}%</span>
                      </div>
                      <Progress value={op.pct_economia} className="h-2" />
                    </div>

                    {/* Top 3 Servizi */}
                    {op.servizi.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {op.servizi.slice(0, 3).map((s) => (
                          <span 
                            key={`servizio-${s.nome}`}
                            className="px-2 py-1 bg-slate-100 rounded-lg text-xs text-slate-600"
                          >
                            {s.nome} <span className="text-slate-400">x{s.count}</span>
                          </span>
                        ))}
                        {op.servizi.length > 3 && (
                          <span className="px-2 py-1 text-xs text-slate-400">
                            +{op.servizi.length - 3} altri
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog Dettaglio */}
      <DettaglioOperatoreDialog 
        open={dettaglioOpen}
        onOpenChange={setDettaglioOpen}
        operatore={selectedOperatore}
        dettaglio={dettaglio}
        loading={loadingDettaglio}
      />
    </Card>
  );
}

// Componente Dialog Dettaglio
function DettaglioOperatoreDialog({ open, onOpenChange, operatore, dettaglio, loading }) {
  if (!operatore) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: operatore.colore }}
            >
              {operatore.nome.charAt(0)}
            </div>
            <div>
              <span className="text-xl">{operatore.nome}</span>
              <p className="text-sm font-normal text-slate-500">Report Dettagliato</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : dettaglio ? (
          <div className="space-y-6 pt-4">
            {/* Statistiche Principali */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              <div className="text-center p-3 bg-blue-50 rounded-xl min-w-0">
                <p className="text-base sm:text-2xl font-bold text-blue-700 break-words">€{dettaglio.statistiche.incasso_totale}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Incasso</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-xl min-w-0">
                <p className="text-base sm:text-2xl font-bold text-emerald-700">{dettaglio.statistiche.num_clienti}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Clienti</p>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-xl min-w-0">
                <p className="text-base sm:text-2xl font-bold text-purple-700">{dettaglio.statistiche.num_appuntamenti}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Appuntamenti</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-xl min-w-0">
                <p className="text-base sm:text-2xl font-bold text-amber-700 break-words">€{dettaglio.statistiche.fiche_media}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Fiche Media</p>
              </div>
            </div>

            {/* Servizi Svolti */}
            {dettaglio.servizi.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Scissors className="w-4 h-4 text-purple-600" />
                  Servizi Svolti
                </h4>
                <div className="space-y-2">
                  {dettaglio.servizi.map((s) => (
                    <div 
                      key={`dettaglio-servizio-${s.nome}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                          <span className="text-purple-600 font-bold text-sm">{s.count}</span>
                        </div>
                        <span className="font-medium text-slate-700">{s.nome}</span>
                      </div>
                      <span className="font-bold text-slate-800">€{s.incasso.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Clienti Serviti */}
            {dettaglio.clienti && dettaglio.clienti.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  Clienti Serviti (Top 10)
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {dettaglio.clienti.slice(0, 10).map((c, idx) => (
                    <div 
                      key={c.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                          {c.nome.split(' ').map(n => n.charAt(0)).join('').slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-700">{c.nome}</p>
                          {c.telefono && (
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {c.telefono}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-800">€{c.incasso}</p>
                        <p className="text-xs text-slate-400">{c.num_appuntamenti} app.</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ultimi Appuntamenti */}
            {dettaglio.appuntamenti && dettaglio.appuntamenti.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  Ultimi Appuntamenti
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dettaglio.appuntamenti.slice(0, 5).map((a, idx) => (
                    <div 
                      key={a.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-slate-700">{a.cliente_nome}</p>
                        <p className="text-xs text-slate-400">
                          {format(new Date(a.data), "dd/MM/yyyy", { locale: it })} • {a.ora}
                        </p>
                      </div>
                      <span className="font-bold text-emerald-600">€{a.totale}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center py-8 text-slate-500">Nessun dato disponibile</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
